import type { Task } from "@app/core";
import { Button, cn, Plus } from "@app/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInboxDragStore, useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { useEffectivePriorityCtx, useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { ShowMoreFooter, usePagedRows } from "../../lib/paged-list.js";
import { useViewFilters } from "../../lib/useViewFilters.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useI18n } from "../../lib/useI18n.js";
import { priorityToneClass } from "../task-colors.js";
import { washStyle } from "../block-wash.js";
import { CalendarRenderIndexProvider, useCalendarInheritedTask, useCalendarRenderIndex } from "../calendar-view/CalendarRenderIndex.js";
import { getDragTaskId, setDragTaskId, setTaskDragging, splitVirtualId } from "../calendar-view/drag.js";
import { calendarTouchDragEnd, calendarTouchDragMove, calendarTouchDrop, setInboxTouchTarget } from "../calendar-view/touch-drop.js";
import { TaskBlockContent } from "../calendar-view/TaskBlockContent.js";
import { sortInboxTasks, tasksForInbox, type InboxSort } from "./inbox-list.js";

const LONG_PRESS_MS = 250;
const MOVE_SLOP_PX = 10;

/** Max rendered height of an inbox block ≈ 4 text lines. */
const MAX_H = 80;
const BLOCK_W = 220;

/** The undated backlog. Items render with the EXACT same block style as the
 *  week/day calendar (`TaskBlockContent`: title, duration, notes, labels, nested
 *  subtasks, same keep-priority degradation), in their own color, up to 4 lines.
 *  Draggable onto the calendar to schedule; the panel is also a drop target that
 *  un-schedules a dropped block. */
export function InboxPanel({ onScheduledFromDrag }: { onScheduledFromDrag?: () => void } = {}) {
  const { t } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const edit = useTasksStore((s) => s.edit);
  const applyFilters = useViewFilters();
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const mobile = useMobileUx();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sort, setSort] = useState<InboxSort>("default");
  // Day-anchored context so the priority/urgency sorts derive from deadlines
  // exactly like the sidebar filter (updates at the day boundary).
  const urgencyCtx = useEffectivePriorityCtx();
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  // Mobile: while this panel is mounted (the open inbox drawer), register it as a
  // touch drop target so a long-pressed calendar block dragged onto it gets
  // un-scheduled (the desktop drag→inbox drop, for touch). Reads live store state
  // so no re-registration on every edit.
  useEffect(() => {
    if (!mobile) return;
    setInboxTouchTarget({
      hit: (x, y) => {
        const el = rootRef.current;
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      },
      drop: (taskId, x, y) => {
        const el = rootRef.current;
        if (!el) return false;
        const r = el.getBoundingClientRect();
        if (x < r.left || x > r.right || y < r.top || y > r.bottom) return false;
        const { sourceId } = splitVirtualId(taskId);
        const st = useTasksStore.getState();
        const src = st.tasks.find((t) => t.id === sourceId);
        // Un-schedule a scheduled one-off (recurring can't be inbox'd); consume
        // the drop either way so it isn't also rescheduled on the grid.
        if (src && !src.recurrence_id && src.due_at) void st.edit(sourceId, { due_at: null, all_day: false });
        return true;
      },
    });
    return () => setInboxTouchTarget(null);
  }, [mobile]);

  // Honour the same calendar-visibility + label/topic/priority/urgency filters
  // as the views. Memoized: this panel is mounted alongside the calendar and
  // re-renders with it, and re-filtering + re-sorting the whole task list on
  // every one of those renders is pure waste.
  const items = useMemo(
    () => sortInboxTasks(tasksForInbox(applyFilters(tasks)), sort, urgencyCtx),
    [applyFilters, tasks, sort, urgencyCtx],
  );
  // A real backlog is thousands of items, and each row renders a full calendar
  // block (`TaskBlockContent`). Page it like every other list in the app.
  const { shown, total, hasMore, showMore } = usePagedRows(items, sort);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const dropped = getDragTaskId(e);
    if (!dropped) return;
    const { sourceId } = splitVirtualId(dropped);
    const src = tasks.find((t) => t.id === sourceId);
    // Un-schedule a scheduled one-off (keeps its event-ness; the inbox shows both).
    if (src && !src.recurrence_id && src.due_at) void edit(sourceId, { due_at: null, all_day: false });
  }

  return (
    <CalendarRenderIndexProvider>
    <div
      ref={rootRef}
      data-inbox-drawer
      className={`flex h-full flex-col rounded ${dragOver ? "ring-2 ring-primary/50" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="mb-1 flex items-center gap-2 px-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">{t("common.inbox")}</span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as InboxSort)}
          aria-label={t("inbox.sort")}
          className="ml-auto max-w-[7.5rem] rounded border bg-background px-1 py-0.5 text-xs text-muted-foreground"
        >
          <option value="default">{t("inbox.sort.default")}</option>
          <option value="priority">{t("inbox.sort.priority")}</option>
          <option value="urgency">{t("inbox.sort.urgency")}</option>
          <option value="title">{t("inbox.sort.title")}</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => openCreate(undefined, undefined, false)}>
          <Plus className="h-4 w-4" /> {t("common.new")}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="px-1 py-2 text-sm text-muted-foreground">{t("inbox.panelEmpty")}</p>
      ) : (
        <ul className="flex flex-col gap-1 overflow-y-auto">
          {shown.map((t) => <InboxRow key={t.id} task={t} onScheduledFromDrag={onScheduledFromDrag} />)}
          {hasMore && <li><ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} className="border-t-0 px-1" /></li>}
        </ul>
      )}
    </div>
    </CalendarRenderIndexProvider>
  );
}

function InboxRow({ task, onScheduledFromDrag }: { task: Task; onScheduledFromDrag?: (() => void) | undefined }) {
  const { t } = useI18n();
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const index = useCalendarRenderIndex();
  const inheritedTask = useCalendarInheritedTask(task);
  const mobile = useMobileUx();
  const done = task.status === "done";
  const contextColor = index.contextColorOf(task);
  const { tint, gradient } = index.blockStyle;
  // Matrix scope: continuous diagonal color for the priority accent/fill
  // (placed-only — see useMatrixPriorityColor).
  const priorityColor = useMatrixPriorityColor(inheritedTask);
  const { vars: customStyle, washed: hasCustomStyle } = washStyle(inheritedTask, contextColor, tint, gradient, priorityColor);

  // Mobile touch-drag: long-press picks the item up, the finger drags it over
  // the calendar (drawer goes transparent), release schedules at the drop slot.
  // A short tap (no drag) opens the editor. Once the long-press arms, WINDOW
  // listeners own the drag (passive:false so the move can preventDefault the
  // list scroll); a pre-arm move cancels the pickup so the list still scrolls.
  // Desktop keeps native HTML5 DnD.
  const touch = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> | null; armed: boolean }>({ x: 0, y: 0, timer: null, armed: false });

  function onTouchStart(e: React.TouchEvent) {
    if (!mobile) return;
    const t = e.touches[0];
    if (!t) return;
    const s = touch.current;
    s.x = t.clientX; s.y = t.clientY; s.armed = false;
    s.timer = setTimeout(() => {
      s.timer = null;
      s.armed = true;
      useInboxDragStore.getState().start(task.id, task.title, Math.max(task.duration_minutes, 60), s.x, s.y);
      try { navigator.vibrate?.(15); } catch { /* unsupported */ }
      window.addEventListener("touchmove", winMove, { passive: false });
      window.addEventListener("touchend", winEnd);
      window.addEventListener("touchcancel", winEnd);
    }, LONG_PRESS_MS);
  }
  function winMove(ev: TouchEvent) {
    const t = ev.touches[0];
    if (!t) return;
    ev.preventDefault();
    useInboxDragStore.getState().move(t.clientX, t.clientY);
    // Feed the grid so a finger held at its left/right edge pages the day window
    // (same edge auto-paging as a calendar block move).
    calendarTouchDragMove(t.clientX, t.clientY);
  }
  function winEnd(ev: TouchEvent) {
    window.removeEventListener("touchmove", winMove);
    window.removeEventListener("touchend", winEnd);
    window.removeEventListener("touchcancel", winEnd);
    touch.current.armed = false;
    calendarTouchDragEnd();
    const t = ev.changedTouches[0];
    const dropped = t ? calendarTouchDrop(task.id, t.clientX, t.clientY) : false;
    useInboxDragStore.getState().end();
    if (dropped) onScheduledFromDrag?.();
  }
  function onTouchMove(e: React.TouchEvent) {
    if (!mobile || touch.current.armed) return;
    const t = e.touches[0];
    const s = touch.current;
    if (t && (Math.abs(t.clientX - s.x) > MOVE_SLOP_PX || Math.abs(t.clientY - s.y) > MOVE_SLOP_PX)) {
      if (s.timer) { clearTimeout(s.timer); s.timer = null; }
    }
  }
  function onTouchEnd() {
    if (!mobile) return;
    const s = touch.current;
    // A quick tap (long-press never armed) opens the editor; an armed drag is
    // finished by winEnd, so do nothing here.
    if (s.timer) { clearTimeout(s.timer); s.timer = null; openEdit(task); }
  }

  return (
    <li
      draggable={!mobile}
      onDragStart={(e) => { setDragTaskId(e, task.id); setTaskDragging(true); }}
      onDragEnd={() => setTaskDragging(false)}
      onClick={mobile ? undefined : () => openEdit(task)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={hasCustomStyle ? customStyle : undefined}
      className={cn(
        "group relative cursor-grab overflow-hidden rounded border px-1.5 pt-0.5 pb-2 shadow-sm transition-[filter] hover:brightness-110 active:cursor-grabbing",
        hasCustomStyle && "task-wash",
        !hasCustomStyle && priorityToneClass(task.priority, "block"),
        done && "opacity-50 line-through",
      )}
      title={mobile ? t("inbox.longPressTitle") : t("inbox.dragTitle")}
    >
      <div style={{ maxHeight: MAX_H }} className="overflow-hidden">
        <TaskBlockContent task={task} blockHeightPx={MAX_H} blockWidthPx={BLOCK_W} expanded={false} />
      </div>
    </li>
  );
}
